import { HUImageDtoSchema } from "@/modules/dtos/imageDto"
import { StripeAccountStatus } from "@/modules/payment/stripeTypes"
import { AddressDtoSchema, type AddressDto } from "@modules/address/addressDto"
import { PhoneNumberWithPrefixSchema, type PhoneNumberWithPrefix } from "@modules/box/boxTypes"
import { BoxStaffRole, BoxStaffStatus } from "@modules/boxStaff/gateway/boxStaffDto"
import { GenderCategory, UserBadgeDtoSchema } from "@modules/profile/publicProfile/gateway/publicProfileDto"
import { z } from "zod"

export const HidableAttributeGenderCategorySchema = z
  .object({
    value: z.enum(GenderCategory),
    visibility: z.object({
      all: z.boolean(),
    }),
  })
  .meta({ id: "HidableAttributeGenderCategorySchema" })
export type HidableAttributeGenderCategory = z.infer<typeof HidableAttributeGenderCategorySchema>

export const HidableAttributeStringSchema = z
  .object({
    value: z.string(),
    visibility: z.object({
      all: z.boolean(),
    }),
  })
  .meta({ id: "HidableAttributeStringSchema" })
export type HidableAttributeString = z.infer<typeof HidableAttributeStringSchema>

export const HidableAttributeFloatSchema = z
  .object({
    value: z.number(),
    visibility: z.object({
      all: z.boolean(),
    }),
  })
  .meta({ id: "HidableAttributeFloatSchema" })
export type HidableAttributeFloat = z.infer<typeof HidableAttributeFloatSchema>

export const BoxesStaffMemberSchema = z
  .object({
    boxId: z.string(),
    boxName: z.string(),
    role: z.enum(BoxStaffRole),
    status: z.enum(BoxStaffStatus),
  })
  .meta({ id: "BoxesStaffMemberSchema" })
export type BoxesStaffMember = z.infer<typeof BoxesStaffMemberSchema>

export const UserProfileDtoSchema = z
  .object({
    id: z.string(),
    avatar: HUImageDtoSchema.nullable(),
    email: z.string(),
    firstname: z.string(),
    lastname: z.string(),
    username: z.string(),
    locale: z.string(),
    inscriptionDate: z.string(),
    notificationsEnabled: z.boolean(),
    stripeAccountStatus: z.enum(StripeAccountStatus),
    stripeAccountId: z.string().nullable(),
    stripeAccountCurrency: z.string().nullable(),
    stripeMinimumAmount: z.number().nullable(),
    stripeMaximumAmount: z.number().nullable(),
    biography: z.string().nullable(),
    birthdate: HidableAttributeStringSchema.nullable(),
    crossfitStartDate: HidableAttributeStringSchema.nullable(),
    genderCategory: HidableAttributeGenderCategorySchema.nullable(),
    job: HidableAttributeStringSchema.nullable(),
    nationality: HidableAttributeStringSchema.nullable(),
    height: HidableAttributeFloatSchema.nullable(),
    weight: HidableAttributeFloatSchema.nullable(),
    address: AddressDtoSchema.nullable(),
    phoneNumber: PhoneNumberWithPrefixSchema.nullable(),
    badges: UserBadgeDtoSchema.array(),
    emergencyInformation: z.string().nullable(),
    boxesStaffMember: z.array(BoxesStaffMemberSchema),
  })
  .meta({ id: "UserProfileDtoSchema" })
export type UserProfileDto = z.infer<typeof UserProfileDtoSchema>

export type UserProfileDraftDto = {
  firstname: string
  lastname: string
  username: string
  imageKey?: string
  nationality?: HidableAttributeString
  job?: HidableAttributeString
  biography?: string
  address?: AddressDto
  phoneNumber?: PhoneNumberWithPrefix
  weight?: HidableAttributeFloat
  height?: HidableAttributeFloat
  crossfitStartDate?: HidableAttributeString
  genderCategory?: HidableAttributeGenderCategory
  emergencyInformation?: string
}
